import { type RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * Tracks the live content-box width (px) of an element via ResizeObserver.
 *
 * Use this to make a component respond to its own container width instead of the
 * viewport — e.g. a toolbar that must reflow when a side panel narrows its column,
 * which viewport breakpoints (`xl:` etc.) cannot detect.
 *
 * Returns a ref to attach to the measured element and its latest width (0 until
 * the first measurement, which runs synchronously before paint).
 */
export function useElementWidth<T extends HTMLElement = HTMLElement>(): [
  RefObject<T | null>,
  number,
] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = (): void => setWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
